import {Injectable} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {AgendaActivity} from '@model/agenda-activity';
import {SchoolDiaryEntryEvent} from '@model/teacher-planning-event';
import {Profile} from '@model/profile';
import {Schedule} from '@model/schedule';
import {DateTime} from 'luxon';
import {map, switchMap, take} from 'rxjs/operators';
import {EvaluationView} from '@model/evaluation';
import {SchoolReportForStudent} from '@model/school-report-for-student';
import {SchoolReportType} from '@model/school-report-type';
import {School} from '@model/school';
import {EndOfYearDecision} from '@model/end-of-year-decision';
import {StudentRemark} from '@model/student-remark';
import {StudentService} from '@services/student.service';
import {Store} from '@ngrx/store';
import {getPlanningEvents, StoreState} from '@redux/reducers';
import {teacherPlanningEventSaved} from '@redux/actions/teacher-planning.action';
import {LegalStudentAbsence} from '@model/legal-student-absence';
import {CertificationPerUnit} from '@model/certification-per-unit';
import {StudentAttribution} from '@model/student-attribution';
import {SchoolReportSnapshots} from "@model/school-reports-snapshots";
import {StudentDetention} from '@model/student-detention';
import {SchoolSetting} from "@model/school-setting";
import {SharedDocument} from '@model/shared-documents';
import {Holiday} from '@model/holiday';
import {StudentLoadedAction} from '@redux/actions/students.action';

@Injectable()
export class StudentFacadeService {
  school$: Observable<School>;
  elementarySchool$: Observable<boolean>;
  profile$: Observable<Profile>;
  schoolSettings$: Observable<SchoolSetting[]>;
  agendaActivities$: Observable<AgendaActivity[]>;
  planningEventsByHoursByDates$: Observable<{ [hour: string]: { [date: string]: SchoolDiaryEntryEvent[] }; }>;
  planningEvents$: Observable<SchoolDiaryEntryEvent[]>;
  schedule$: Observable<Schedule>;
  isMobile$: Observable<boolean>;
  studentEvaluations$: Observable<EvaluationView[]>;
  schoolReportForStudent$: Observable<SchoolReportForStudent>;
  schoolReportTypesByClassId$: Observable<{ [classId: number]: SchoolReportType }>;
  studentEndOfYearDecision$: Observable<EndOfYearDecision>;
  studentRemarks$: Observable<StudentRemark[]>;
  studentAbsencesByDayByHour$: Observable<any[]>;
  studentLegalAbsences$: Observable<LegalStudentAbsence[]>;
  certificationsPerUnit$: Observable<CertificationPerUnit[]>;
  certificationPerUnitForStudent$: Observable<CertificationPerUnit>;
  studentAttributions$: Observable<StudentAttribution[]>;
  studentSchoolReportSnapshots$: Observable<SchoolReportSnapshots[]>;
  studentDetentions$: Observable<StudentDetention[]>;
  sharedDocuments$: Observable<SharedDocument[]>;
  holidays$: Observable<Holiday[]>;
  holidaysForStudent$: Observable<Holiday[]>;

  constructor(
    private _store: Store<StoreState>,
    private _sharedFacade: SharedFacadeService,
    private _studentService: StudentService) {
    this.school$ = this._sharedFacade.school$;
    this.profile$ = this._sharedFacade.profile$;
    this.elementarySchool$ = this._sharedFacade.elementarySchool$;
    this.schoolSettings$ = this._sharedFacade.schoolSettings$;
    this.agendaActivities$ = this._sharedFacade.agendaActivities$;
    this.planningEventsByHoursByDates$ = this._sharedFacade.planningEventsByHoursByDates$;
    this.schedule$ = this._sharedFacade.schedule$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.studentEvaluations$ = this._sharedFacade.studentEvaluations$;
    this.schoolReportForStudent$ = this._sharedFacade.schoolReportForStudent$;
    this.schoolReportTypesByClassId$ = this._sharedFacade.schoolReportTypesByClassId$;
    this.studentEndOfYearDecision$ = combineLatest([
      this._sharedFacade.studentEndOfYearDecisions$,
      this.profile$,
    ]).pipe(
      map(([endOfYearDecisions, profile]) => endOfYearDecisions.find(endOfYearDecision => endOfYearDecision?.studentId === profile?.id))
    );
    this.studentRemarks$ = this._sharedFacade.studentRemarks$.pipe(map(page => page.content));
    this.studentAbsencesByDayByHour$ = this._sharedFacade.studentAbsencesByDayByHour$;
    this.studentLegalAbsences$ = this._sharedFacade.studentLegalAbsences$;
    this.certificationsPerUnit$ = this._sharedFacade.certificationsPerUnit$;
    this.certificationPerUnitForStudent$ = this._sharedFacade.certificationPerUnitForStudent$;
    this.studentAttributions$ = this._sharedFacade.studentAttributions$;
    this.studentSchoolReportSnapshots$ = this._sharedFacade.studentSchoolReportSnapshots$;
    this.studentDetentions$ = this._sharedFacade.studentDetentions$;
    this.sharedDocuments$ = this._sharedFacade.sharedDocuments$;
    this.holidays$ = this._sharedFacade.holidays$;
    this.holidaysForStudent$ = this._sharedFacade.holidaysForSelectedStudent$;
    this.planningEvents$ = this._store.select(getPlanningEvents);
  }

  loadAgendaActivities() {
    this._sharedFacade.loadAgendaActivities();
  }

  loadSchoolDiary(startDate: DateTime, endDate: DateTime) {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadSchoolDiaryForStudent(profile.id, startDate, endDate));
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }

  loadStudentEvaluations() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentEvaluationsForStudent(profile.id));
  }

  loadSchoolReportForStudent() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadSchoolReportForStudent(profile.id));
  }

  loadSchoolReportTypesByClassId() {
    this._sharedFacade.loadSchoolReportTypes();
    this._sharedFacade.loadSchoolReportTypeToClassAssignments();
  }

  loadStudentEndOfYearDecision() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentEndOfYearDecisionForStudent(profile.id));
  }

  loadStudentRemarks() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentRemarksForStudent(profile.id));
  }

  updateSchoolDiaryEntryEvent(schoolDiaryEntryEvent: SchoolDiaryEntryEvent) {
    combineLatest([
      this.profile$,
      this.school$,
    ]).pipe(
      take(1),
      switchMap(([profile, school]) => this._studentService.updateSchoolDiaryEntryEvent(school.id, profile.id, schoolDiaryEntryEvent.id, schoolDiaryEntryEvent.homeworkDone, schoolDiaryEntryEvent.remark))
    ).subscribe(_ => {
      this._store.dispatch(teacherPlanningEventSaved({planningEvent: {
        ...schoolDiaryEntryEvent
      }}))
    });
  }

  loadStudentAbsences() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentAbsencesForStudent(profile.id));
  }

  loadStudentLegalAbsences() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentLegalAbsencesForStudent(profile.id));
  }


  loadCertificationPerUnitWithResultsForStudent(certificationPerUnitId: string) {
    this.profile$.pipe(
      take(1)
    ).subscribe(
      profile => this._sharedFacade.loadCertificationPerUnitWithResultsForStudent(certificationPerUnitId, profile.id)
    );
  }

  loadCertificationsPerUnit() {
    this.profile$.pipe(
      take(1)
    ).subscribe(
      profile => this._sharedFacade.loadCertificationsPerUnitForStudent(profile.id)
    );
  }

  loadStudentAttributions() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentAttributions(profile.id));
  }

  downloadStudentSchoolReportSnapshot(report: SchoolReportSnapshots) {
    this._sharedFacade.downloadStudentSchoolReportSnapshot(report);
  }

  loadStudentSchoolReportSnapshots() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentSchoolReportSnapshots(profile.id))
  }

  loadStudentDetentions() {
    this.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadStudentDetentionsByStudent(profile.id));
  }

  loadSchoolSettings(): void {
    this._sharedFacade.loadSchoolSettings();
  }

  loadSharedDocuments(): void {
    this._sharedFacade.loadSharedDocuments();
  }

  downloadSharedDocument(file: SharedDocument): void {
    this._sharedFacade.downloadSharedDocument(file);
  }

  downloadLegalAbsenceDocument(studentId: number, justificationId: number, documentId: string): void {
    this._sharedFacade.downloadLegalAbsenceDocument(studentId, justificationId, documentId);
  }

  loadHolidaysForCurrentSchoolYear() {
    this._sharedFacade.loadHolidaysForCurrentSchoolYear();
  }

  loadHorizonClassLinks() {
    this._sharedFacade.loadHorizonClassLinks();
  }

  loadDetailsForConnectedStudent() {
    combineLatest([
      this.school$,
      this.profile$,
    ]).pipe(
      take(1),
      switchMap(([school, profile]) => this._studentService.getStudent(school.id, profile.id))
    ).subscribe(student => {
      this._store.dispatch(new StudentLoadedAction(student));
    });
  }
}
